import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Company, CompanySize } from '../../../types';
import { assetsApi } from '../api';
import { usersApi } from '../api/users.api';
import { useModal, ConfirmModal, CreateCompanyWizard, CompanyDetailsModal, EditCompanyModal } from '@components/modal';
import { notify } from '@core/notifications/notify';
import {
  Building,
  Plus,
  Search,
  Filter,
  Trash2,
  X,
  RefreshCw,
  AlertTriangle,
  Eye,
  FileText,
  BarChart3,
  Download,
  ClipboardCheck,
  UserCheck,
  XCircle,
  ChevronDown,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ── Auditor assignment API helpers ─────────────────────────────────────── */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1] as string) : null;
}

function getAuthHeaders(includeCsrf = false): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token');
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  if (includeCsrf) { const csrf = getCsrfToken(); if (csrf) headers['X-CSRF-Token'] = csrf; }
  return headers;
}

async function fetchAuditorAssignment(companyId: number): Promise<any | null> {
  try {
    const res = await fetch(`/api/rbac/auditor-assignments/company/${companyId}`, { headers: getAuthHeaders(false) });
    if (!res.ok) return null;
    const data = await res.json();
    const active = (data.data || []).find((a: any) => a.status === 'active');
    return active || null;
  } catch { return null; }
}

async function createAuditorAssignment(userId: number, companyId: number, reason: string): Promise<void> {
  const res = await fetch('/api/rbac/auditor-assignments', {
    method: 'POST',
    headers: getAuthHeaders(true),
    body: JSON.stringify({ userId, companyId, reason }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to assign auditor');
}

async function revokeAuditorAssignment(assignmentId: number): Promise<void> {
  const res = await fetch(`/api/rbac/auditor-assignments/${assignmentId}/revoke`, {
    method: 'POST',
    headers: getAuthHeaders(true),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Failed to revoke assignment');
}

const Companies: React.FC = () => {
  const { hasPermission } = useAuth();
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const wizardModalIdRef = React.useRef<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);
  // Map: company_id → primary admin user
  const [companyAdminMap, setCompanyAdminMap] = useState<Record<number, { name: string; email: string }>>({});
  // Map: company_id → active auditor assignment
  const [companyAuditorMap, setCompanyAuditorMap] = useState<Record<number, any>>({});
  // All external auditors available to assign
  const [externalAuditors, setExternalAuditors] = useState<any[]>([]);

  useEffect(() => {
    loadCompanies();
    loadCompanyAdmins();
    loadExternalAuditors();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await assetsApi.getCompanies();
      setCompanies(data);
      loadAuditorAssignmentsForCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyAdmins = async () => {
    try {
      // Load all users; find the first active user per company that has a company-admin profile
      const result = await usersApi.getUsers(1, 500, '');
      const userList = result.users || [];
      const adminMap: Record<number, { name: string; email: string }> = {};
      for (const u of userList) {
        if (u.company_id && u.is_active && !adminMap[u.company_id]) {
          // Heuristic: users without company_id are system/auditor users;
          // first active user per company shown as primary contact/admin
          adminMap[u.company_id] = {
            name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
            email: u.email
          };
        }
      }
      setCompanyAdminMap(adminMap);
    } catch {
      // Non-critical — degrade gracefully
    }
  };

  const loadExternalAuditors = async () => {
    try {
      const result = await usersApi.getUsers(1, 200, '');
      const auditors = (result.users || []).filter((u: any) =>
        !u.company_id && u.role?.toLowerCase().includes('auditor')
      );
      setExternalAuditors(auditors);
    } catch { /* non-critical */ }
  };

  const loadAuditorAssignmentsForCompanies = async (companyList: Company[]) => {
    const map: Record<number, any> = {};
    await Promise.all(
      companyList.map(async (c) => {
        const assignment = await fetchAuditorAssignment(c.id);
        if (assignment) map[c.id] = assignment;
      })
    );
    setCompanyAuditorMap(map);
  };

  const handleAssignAuditor = (company: Company) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => {
        const [selectedAuditorId, setSelectedAuditorId] = useState<number | ''>('');
        const [reason, setReason] = useState('');
        const [saving, setSaving] = useState(false);
        const currentAssignment = companyAuditorMap[company.id];

        const handleAssign = async () => {
          if (!selectedAuditorId) return;
          setSaving(true);
          try {
            await createAuditorAssignment(Number(selectedAuditorId), company.id, reason || 'Assigned by system admin');
            notify.success(`External auditor assigned to ${company.name}`);
            loadAuditorAssignmentsForCompanies(companies);
            onClose();
          } catch (err: any) {
            notify.error(err.message || 'Failed to assign auditor');
          } finally { setSaving(false); }
        };

        const handleRevoke = async () => {
          if (!currentAssignment) return;
          setSaving(true);
          try {
            await revokeAuditorAssignment(currentAssignment.id);
            notify.success('Auditor assignment revoked');
            loadAuditorAssignmentsForCompanies(companies);
            onClose();
          } catch (err: any) {
            notify.error(err.message || 'Failed to revoke assignment');
          } finally { setSaving(false); }
        };

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="bg-blue-600 p-5">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="text-white" size={20} />
                  <div>
                    <h2 className="text-base font-bold text-white">Assign External Auditor</h2>
                    <p className="text-blue-100 text-sm">{company.name}</p>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Current assignment */}
                {currentAssignment && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1">Current Auditor</p>
                    <p className="font-semibold text-slate-800 text-sm">
                      {currentAssignment.first_name} {currentAssignment.last_name}
                    </p>
                    <p className="text-xs text-slate-500">{currentAssignment.auditor_email}</p>
                    <button
                      onClick={handleRevoke}
                      disabled={saving}
                      className="mt-2 flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      <XCircle size={13} /> Revoke Assignment
                    </button>
                  </div>
                )}

                {/* Select auditor */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {currentAssignment ? 'Replace with another auditor' : 'Select External Auditor'}
                  </label>
                  {externalAuditors.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                      No external auditors available. Create a user with the <strong>External Auditor</strong> profile first.
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        value={selectedAuditorId}
                        onChange={e => setSelectedAuditorId(e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 text-sm appearance-none bg-white pr-10"
                      >
                        <option value="">-- Select an auditor --</option>
                        {externalAuditors.map((a: any) => (
                          <option key={a.id} value={a.id}>
                            {a.first_name} {a.last_name} ({a.email})
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason (optional)</label>
                  <input
                    type="text"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Annual ISO 27001 certification audit"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 text-sm"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={!selectedAuditorId || saving}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? <RefreshCw size={14} className="animate-spin" /> : <UserCheck size={14} />}
                    {saving ? 'Assigning...' : 'Assign Auditor'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      },
      props: {},
    });
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalCompanies = companies.length;
    const activeCompanies = companies.filter(c => c.is_active).length;
    const inactiveCompanies = totalCompanies - activeCompanies;

    // Recent companies (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentCompanies = companies.filter(c => {
      const dateVal = (c as any).createdAt || c.created_at;
      if (!dateVal) return false;
      const d = new Date(dateVal);
      return !isNaN(d.getTime()) && d > thirtyDaysAgo;
    }).length;

    return {
      totalCompanies,
      activeCompanies,
      inactiveCompanies,
      recentCompanies,
      avgCompaniesPerMonth: Math.round(totalCompanies / 12),
    };
  }, [companies]);

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.legal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const _getIndustryColor = (industry: string) => {
    const colors: Record<string, string> = {
      'Manufacturing': 'bg-blue-100 text-blue-800 border-blue-200',
      'Energy': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Transportation': 'bg-green-100 text-green-800 border-green-200',
      'Agriculture': 'bg-orange-100 text-orange-800 border-orange-200',
      'Construction': 'bg-slate-100 text-slate-800 border-slate-200',
      'Financial Services': 'bg-purple-100 text-purple-800 border-purple-200',
      'Healthcare': 'bg-red-100 text-red-800 border-red-200',
      'Technology': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Retail': 'bg-pink-100 text-pink-800 border-pink-200',
      'General': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[industry] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const _getSizeColor = (size: CompanySize) => {
    const colors: Record<CompanySize, string> = {
      'small': 'bg-emerald-100 text-emerald-800',
      'medium': 'bg-amber-100 text-amber-800',
      'large': 'bg-orange-100 text-orange-800',
      'enterprise': 'bg-red-100 text-red-800'
    };
    return colors[size] || 'bg-gray-100 text-gray-800';
  };

  const handleCreateCompany = async (companyData: any) => {
    try {
      await assetsApi.createCompany(companyData);
      loadCompanies();
      notify.success('Company created successfully!');
    } catch (error) {
      console.error('Failed to create company:', error);
      notify.error('Failed to create company. Please try again.');
    }
  };

  const handleWizardComplete = (companyId: number) => {
    closeModal(wizardModalIdRef.current!);
    notify.success('Company created successfully!');
    loadCompanies();
    navigate(`/admin/companies/${companyId}`);
  };

  const handleDeleteCompany = async (companyId: number) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => {
        const [exportData, setExportData] = useState(true);
        const [loading, setLoading] = useState(false);

        const handleConfirm = async () => {
          setLoading(true);
          try {
            const result = await assetsApi.deleteCompany(companyId, exportData);
            setCompanies(companies.filter(c => c.id !== companyId));

            if (result.exportData) {
              notify.success('Company deleted successfully. Data export is available for download.');
            } else {
              notify.success('Company deleted successfully.');
            }

            onClose();
          } catch (error) {
            console.error('Failed to delete company:', error);
            notify.error('Failed to delete company. Please try again.');
          } finally {
            setLoading(false);
          }
        };

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4">
              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                  <h2 className="text-lg font-bold text-slate-900">Delete Company</h2>
                </div>
              </div>

              <div className="p-5">
                <p className="text-slate-600 text-sm mb-4">
                  Are you sure you want to delete this company? This action cannot be undone.
                </p>

                <div className="flex items-center mb-5">
                  <input
                    type="checkbox"
                    id="exportData"
                    checked={exportData}
                    onChange={(e) => setExportData(e.target.checked)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="exportData" className="ml-2 text-sm text-gray-700 flex items-center">
                    <Download className="h-4 w-4 mr-1" />
                    Export company data before deletion
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Company'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      },
      props: {},
    });
  };

  const handleViewCompany = (company: Company) => {
    openModal({
      component: CompanyDetailsModal,
      props: {
        company,
        onClose: () => {},
      },
      options: {
        size: 'large',
        animation: 'slide',
      },
    });
  };

  const handleEditCompany = (company: Company) => {
    openModal({
      component: EditCompanyModal,
      props: {
        company,
        onClose: () => {},
        onSubmit: async (data: any) => {
          try {
            await assetsApi.updateCompany(company.id, data);
            loadCompanies();
            openModal({
              component: ConfirmModal,
              props: {
                title: 'Success',
                message: 'Company updated successfully!',
                type: 'success',
                onConfirm: () => {},
                onCancel: () => {},
              },
              options: { size: 'large' }
            });
          } catch (error) {
            console.error('Failed to update company:', error);
            openModal({
              component: ConfirmModal,
              props: {
                title: 'Error',
                message: 'Failed to update company. Please try again.',
                type: 'error',
                onConfirm: () => {},
                onCancel: () => {},
              },
            });
          }
        },
      },
      options: {
        size: 'large',
        animation: 'slide',
      },
    });
  };

  const handleOpenCreateModal = () => {
    const modalId = openModal({
      component: CreateCompanyWizard,
      props: {
        onClose: () => closeModal(modalId),
        onComplete: handleWizardComplete,
      },
      options: {
        size: 'large',
        animation: 'slide',
      },
    });
    wizardModalIdRef.current = modalId;
  };

  if (!hasPermission('companies.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-lg font-bold text-slate-900 mb-1 text-left">Access Denied</h1>
          <p className="text-slate-500 text-sm text-left">Only Super Admin and Compliance Admin can access Company Management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Page Heading */}
        <div className="mb-8 text-left">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Company Management</h1><PageInfoButton /></div>
              <p className="text-slate-500 text-left">Manage {analytics.totalCompanies} company profiles across your compliance platform.</p>
            </div>
            <button
              onClick={handleOpenCreateModal}
              className="px-5 py-3 bg-blue-600 text-white font-bold text-sm rounded-full hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
            >
              <Plus size={16} />
              Create Company
            </button>
          </div>
        </div>

        {/* Stats Row — glass cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">TOTAL</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.totalCompanies}</p>
            <p className="text-[10px] text-slate-400">Registered companies</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">ACTIVE</p>
            <p className="text-2xl font-bold text-emerald-600">{analytics.activeCompanies}</p>
            <p className="text-[10px] text-slate-400">Currently operational</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">INACTIVE</p>
            <p className={`text-2xl font-bold ${analytics.inactiveCompanies > 0 ? 'text-red-500' : 'text-slate-900'}`}>{analytics.inactiveCompanies}</p>
            <p className="text-[10px] text-slate-400">Pending activation</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">RECENT (30D)</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.recentCompanies}</p>
            <p className="text-[10px] text-blue-500 font-bold">{analytics.recentCompanies > 0 ? `↑ ${analytics.recentCompanies} new` : '—'}</p>
          </div>
        </div>

        {/* Companies Table — glass card */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 text-left">Companies</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-8 py-2 bg-slate-50 border-none rounded-full text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all w-56"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={12} />
                  </button>
                )}
              </div>
              <span className="text-xs text-slate-400 font-bold">{filteredCompanies.length} results</span>
            </div>
          </div>

          {filteredCompanies.length === 0 ? (
            <div className="py-12 px-6 text-left">
              <Building className="text-slate-300 mb-3" size={32} />
              <h3 className="text-sm font-bold text-slate-900 mb-1">No Companies Found</h3>
              <p className="text-xs text-slate-500 max-w-md mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first company profile'}
              </p>
              <button onClick={handleOpenCreateModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-full hover:bg-blue-700 transition-colors">
                <Plus size={14} /> Create First Company
              </button>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="text-slate-400 text-[10px] uppercase border-b border-slate-100">
                  <th className="px-6 pb-3 pt-4 font-bold text-left">Company</th>
                  <th className="px-6 pb-3 pt-4 font-bold text-left">Company Admin</th>
                  <th className="px-6 pb-3 pt-4 font-bold text-left">External Auditor</th>
                  <th className="px-6 pb-3 pt-4 font-bold text-left">Status</th>
                  <th className="px-6 pb-3 pt-4 font-bold text-left">Created</th>
                  <th className="px-6 pb-3 pt-4 font-bold text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {filteredCompanies.map((company) => (
                  <tr key={company.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 text-left">
                      <button
                        onClick={() => navigate(`/admin/companies/${company.id}`)}
                        className="text-sm font-bold text-slate-700 hover:text-blue-600 text-left transition-colors"
                      >
                        {company.name}
                      </button>
                      <div className="text-[10px] text-slate-400">{company.legal_name}</div>
                      {company.registration_number && (
                        <div className="text-[10px] text-slate-300">Reg: {company.registration_number}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-left">
                      {companyAdminMap[company.id] ? (
                        <div>
                          <div className="text-xs font-bold text-slate-700">{companyAdminMap[company.id]!.name}</div>
                          <div className="text-[10px] text-slate-400">{companyAdminMap[company.id]!.email}</div>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">No admin assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-left">
                      {companyAuditorMap[company.id] ? (
                        <button onClick={() => handleAssignAuditor(company)} className="flex items-center gap-2 group text-left">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                          <div>
                            <div className="text-xs font-bold text-slate-700 group-hover:text-blue-600 transition-colors">
                              {companyAuditorMap[company.id].first_name} {companyAuditorMap[company.id].last_name}
                            </div>
                            <div className="text-[10px] text-slate-400">{companyAuditorMap[company.id].auditor_email}</div>
                          </div>
                        </button>
                      ) : (
                        <button onClick={() => handleAssignAuditor(company)} className="text-[10px] font-bold text-blue-600 hover:text-blue-800">
                          Assign Auditor
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-left">
                      <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${
                        company.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                      }`}>
                        {company.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-left text-slate-500">
                      {(company as any).createdAt || company.created_at
                        ? new Date((company as any).createdAt || company.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-left">
                      <button onClick={() => handleDeleteCompany(company.id)} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Companies;
